/**
 * Decode the `exp` claim from a JWT payload.
 *
 * Returns expiration time in milliseconds since Unix epoch, or `null` if the
 * token is malformed, missing the `exp` claim, or the claim is not a number.
 *
 * Does not verify the signature — that is the IdP's responsibility. This is
 * only used to decide whether the cached token should be proactively refreshed
 * before handing it to Intercom.
 */
export const decodeJwtExp = (token: string): number | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payloadSegment = parts[1];
    if (!payloadSegment) return null;

    const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );

    const json = atob(padded);
    const payload = JSON.parse(json) as { exp?: unknown };

    if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) {
      return null;
    }

    return payload.exp * 1000;
  } catch {
    return null;
  }
};
